import { Info } from 'lucide-react';
import { Card, CardContent, Tooltip, TooltipTrigger, TooltipContent } from '@design-system';
import { formatCurrency } from '@shared/formatCurrency';

import { useAddPolicyFromQuote, getCarrierName } from '@/features/guided-financial-planning/hooks';
import {
  usePolicyCost,
  useActiveTotalAllocated,
} from '@/features/guided-financial-planning/stores';
import { FundingProgressBar } from './funding-progress-bar';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS } from '@client/lib/nyc-lif-demo';

export function SelectedPolicyCard() {
  const { flags } = useFeatureFlags();
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();
  const policyCost = usePolicyCost();
  const totalAllocated = useActiveTotalAllocated();

  if (!selectedQuoteInfo) {
    return null;
  }

  const { quote } = selectedQuoteInfo;
  const carrierName = getCarrierName(quote);
  const logo = flags.nycLifeDemo
    ? `/logos/nyl-logo.png`
    : `/logos/${carrierName.toLowerCase()}.svg`;

  return (
    <Card>
      <CardContent className="flex flex-col px-6 py-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="border-primary/20 size-16 shrink-0 select-none rounded-lg border p-2.5">
              <img
                src={logo}
                alt={flags.nycLifeDemo ? 'New York Life' : carrierName}
                className="size-full object-contain"
              />
            </div>

            <div className="flex flex-col gap-0.5">
              <h3 className="text-base font-semibold text-gray-900">
                {flags.nycLifeDemo ? (PRODUCTS as any)?.[carrierName] : quote.productName}
              </h3>
              <p className="text-sm text-gray-500">
                {flags.nycLifeDemo ? 'New York Life' : carrierName}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                >
                  <span className="text-xs font-medium">Total Cost</span>
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs space-y-2 p-3"
              >
                <p className="font-medium">How is this calculated?</p>
                <p>Estimated total premiums from now until your expected care start age.</p>
                <p className="text-gray-300">
                  For limited-pay policies (like 10-Pay), payments stop after the specified period.
                </p>
              </TooltipContent>
            </Tooltip>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(policyCost)}</p>
          </div>
        </div>

        <FundingProgressBar
          allocatedAmount={totalAllocated}
          totalCost={policyCost}
        />
      </CardContent>
    </Card>
  );
}
